import React from 'react';
import { useNavigate } from 'react-router-dom';
import { colors } from '../styles';
import ActionButtons from './ActionButtons';

// Formatear texto de estacionamiento
const formatearEstacionamiento = (estacionamiento) => {
  if (!estacionamiento || typeof estacionamiento !== 'object' || !estacionamiento.tipo) {
    return null;
  }

  const tipoTextos = {
    propio: 'Estacionamiento propio',
    calle: 'En la calle',
    plaza: 'Plaza cercana',
    sin: 'Sin estacionamiento'
  };

  const facilidadTextos = {
    muy_facil: 'Muy facil',
    facil: 'Facil',
    medio: 'Medio',
    dificil: 'Preferible transporte publico'
  };

  let texto = tipoTextos[estacionamiento.tipo] || '';

  if (estacionamiento.tipo === 'propio' && estacionamiento.lugares) {
    texto += ` (${estacionamiento.lugares} lugares)`;
  }

  if (estacionamiento.facilidad) {
    texto += ` · ${facilidadTextos[estacionamiento.facilidad]}`;
  }

  return texto;
};

const styles = {
  card: {
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '0.75rem',
    cursor: 'pointer',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  cardHover: {
    borderColor: colors.accent,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
  header: {
    marginBottom: '0.5rem',
  },
  nombreRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '0.5rem',
  },
  favoriteButton: {
    background: 'none',
    border: 'none',
    padding: '0.25rem',
    cursor: 'pointer',
    fontSize: '1.25rem',
    lineHeight: 1,
    color: colors.textMuted,
    transition: 'color 0.2s, transform 0.2s',
    flexShrink: 0,
  },
  favoriteButtonActive: {
    color: '#fbbf24',
  },
  nombre: {
    fontSize: '1rem',
    fontWeight: 600,
    color: colors.textPrimary,
    margin: 0,
    marginBottom: '0.25rem',
    flex: 1,
  },
  sourceBadge: {
    fontSize: '0.65rem',
    padding: '0.125rem 0.375rem',
    borderRadius: '4px',
    fontWeight: 500,
    whiteSpace: 'nowrap',
  },
  sourceGoogle: {
    backgroundColor: '#e8f0fe',
    color: '#1967d2',
  },
  sourceVerified: {
    backgroundColor: colors.successBg || '#d1fae5',
    color: colors.success || '#059669',
  },
  meta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.8rem',
    color: colors.textSecondary,
    flexWrap: 'wrap',
  },
  rating: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  star: {
    color: '#fbbf24',
  },
  dot: {
    color: colors.textMuted,
  },
  estadoAbierto: {
    color: colors.success,
    fontWeight: 500,
  },
  estadoCerrado: {
    color: colors.error,
    fontWeight: 500,
  },
  estado24h: {
    color: colors.success,
    fontWeight: 500,
    backgroundColor: colors.successBg,
    padding: '0.125rem 0.375rem',
    borderRadius: '4px',
    fontSize: '0.7rem',
  },
  direccion: {
    fontSize: '0.8rem',
    color: colors.textSecondary,
    marginTop: '0.25rem',
  },
  estacionamiento: {
    fontSize: '0.75rem',
    color: colors.textSecondary,
    marginTop: '0.25rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },
  parkingIcon: {
    fontSize: '0.85rem',
  },
  actions: {
    marginTop: '0.75rem',
    paddingTop: '0.75rem',
    borderTop: `1px solid ${colors.border}`,
  },
};

export default function PlaceCard({
  lugar,
  showActions = true,
  perfilId,
  isFavorito = false,
  favoritoId = null,
  onToggleFavorite = null,
  showFavoriteButton = true
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = React.useState(false);
  const [loadingFavorite, setLoadingFavorite] = React.useState(false);

  // Calcular número de WhatsApp según el modelo de datos
  // tieneWhatsapp=true + whatsapp=null => usar teléfono
  // tieneWhatsapp=true + whatsapp=número => usar ese número
  // tieneWhatsapp=false o undefined => no mostrar WhatsApp
  const whatsappNumber = lugar.tieneWhatsapp
    ? (lugar.whatsapp || lugar.telefono)
    : null;

  const handleClick = (e) => {
    // No navegar si se hizo click en los botones de acción
    if (e.target.closest('button')) return;

    const params = new URLSearchParams();
    if (perfilId) params.set('perfil', perfilId);
    const query = params.toString() ? `?${params.toString()}` : '';
    navigate(`/localizador/lugar/${lugar.id}${query}`);
  };

  const handleFavoriteClick = async (e) => {
    e.stopPropagation();
    if (!onToggleFavorite || loadingFavorite) return;

    setLoadingFavorite(true);
    try {
      await onToggleFavorite(lugar.id, isFavorito, favoritoId);
    } finally {
      setLoadingFavorite(false);
    }
  };

  const renderEstado = () => {
    if (lugar.es24h) {
      return <span style={styles.estado24h}>24h</span>;
    }
    if (lugar.estado === 'abierto') {
      return <span style={styles.estadoAbierto}>{lugar.estadoTexto}</span>;
    }
    return (
      <span style={styles.estadoCerrado}>
        {lugar.estadoTexto} {lugar.horarioAbre && `· ${lugar.horarioAbre}`}
      </span>
    );
  };

  return (
    <div
      style={{
        ...styles.card,
        ...(hovered ? styles.cardHover : {}),
      }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={styles.header}>
        <div style={styles.nombreRow}>
          <h3 style={styles.nombre}>{lugar.nombre}</h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            {lugar.source === 'google_cache' ? (
              <span style={{ ...styles.sourceBadge, ...styles.sourceGoogle }}>
                Google Maps
              </span>
            ) : (lugar.source === 'admin' || lugar.source === 'user_suggestion') && lugar.status === 'verificado' && (
              <span style={{ ...styles.sourceBadge, ...styles.sourceVerified }}>
                ✓ Verificado
              </span>
            )}
            {showFavoriteButton && onToggleFavorite && (
              <button
                style={{
                  ...styles.favoriteButton,
                  ...(isFavorito ? styles.favoriteButtonActive : {})
                }}
                onClick={handleFavoriteClick}
                disabled={loadingFavorite}
                title={isFavorito ? 'Quitar de favoritos' : 'Agregar a favoritos'}
              >
                {loadingFavorite ? '...' : (isFavorito ? '★' : '☆')}
              </button>
            )}
          </div>
        </div>
        <div style={styles.meta}>
          {lugar.rating && (
            <>
              <span style={styles.rating}>
                <span style={styles.star}>★</span>
                <span>{lugar.rating}</span>
              </span>
              <span style={styles.dot}>·</span>
            </>
          )}
          {lugar.distancia !== undefined && lugar.distancia !== null && (
            <>
              <span>{lugar.distancia < 1 ? `${Math.round(lugar.distancia * 1000)} m` : `${lugar.distancia} km`}</span>
              <span style={styles.dot}>·</span>
            </>
          )}
          {renderEstado()}
        </div>
        {lugar.direccion && (
          <p style={styles.direccion}>{lugar.direccion}</p>
        )}
        {formatearEstacionamiento(lugar.estacionamiento) && (
          <p style={styles.estacionamiento}>
            <span style={styles.parkingIcon}>🅿️</span>
            {formatearEstacionamiento(lugar.estacionamiento)}
          </p>
        )}
      </div>

      {showActions && (lugar.telefono || whatsappNumber || lugar.lat) && (
        <div style={styles.actions}>
          <ActionButtons
            telefono={lugar.telefono}
            whatsapp={whatsappNumber}
            lat={lugar.lat}
            lng={lugar.lng}
            direccion={lugar.direccion}
          />
        </div>
      )}
    </div>
  );
}
